import { createSelector, PayloadAction } from "@reduxjs/toolkit";
import { UserRoles, UserStatus } from "Data/Objects/UI";
import { IIndexSignature, IItem, ISetFilterPayload, ScanDateFilterOptions, UiState } from "Interfaces";
import { createAppSlice } from "Data/Redux/Helpers";
import { createIdentitySelector } from "@clintonelec/typescriptutils";

const initialState: UiState = {
	selectedItems: [],
	scanDateOption: null,
	filters: {
		USER_ROLE: UserRoles.USER,
		USER_STATUS: UserStatus.ACTIVE
	}
};

export const setScanDateOptionReducer = (state: UiState, action: PayloadAction<ScanDateFilterOptions | null>) => {
	state.scanDateOption = action?.payload;
};

export const clearOrderFormReducer = (state: UiState) => {
	state.scanDateOption = null;
};

export const setSelectedItemsReducer = (state: UiState, action: PayloadAction<string[]>) => {
	state.selectedItems = action?.payload;
};

export const setFilterReducer = (state: UiState, action: PayloadAction<ISetFilterPayload>) => {
	const { filter, value } = action?.payload ?? {};

	if (filter) {
		state.filters[ filter ] = value;
	}
};

// # region Selectors

const getUiState = (state: UiState) => state;

const getAllFilters = (state: UiState) => {
	return state.filters;
};

const getSiderFilters = (state: UiState, itemFilters: IIndexSignature<string | number>) => {
	const {
		"filter[order]": orderNumber, "filter[scannedAfter]": scannedAfter, "filter[scannedBefore]": scannedBefore,
		"filter[partNumber]": partNumbers, "filter[serialNumber]": serialNumber,
		"filter[sequenceBefore]": serialRangeUpper, "filter[sequenceAfter]": serialRangeLower,
		"filter[dateCodeBefore]": serialDateUpper, "filter[dateCodeAfter]": serialDateLower
	} = itemFilters;

	return {
		orderNumber: orderNumber ? orderNumber.toString() : "",
		scannedAfter: scannedAfter ? scannedAfter.toString() : null,
		scannedBefore: scannedBefore ? scannedBefore.toString() : null,
		scanDateOption: state.scanDateOption,
		selectedPartNumbers: partNumbers ? partNumbers.toString().split(",") : [],
		serialNumber: serialNumber ? serialNumber.toString() : "",
		serialRangeUpper: serialRangeUpper ? serialRangeUpper.toString() : "",
		serialRangeLower: serialRangeLower ? serialRangeLower.toString() : "",
		serialDateUpper: serialDateUpper ? serialDateUpper.toString().replace(/(.{2})/g, "$1/") : null,
		serialDateLower: serialDateLower ? serialDateLower.toString().replace(/(.{2})/g, "$1/") : null
	};
};

const getSelectedItemIds = (state: UiState) => {
	return state.selectedItems;
};

const filterSelectedItems = (state: UiState, items: IItem[]) => {
	return items.filter(item => state.selectedItems.includes(item.id));
};

const getAllSelectedItems = createSelector(
	[ getUiState, createIdentitySelector<UiState, IItem[]>() ],
	filterSelectedItems
);

const getSiderFilterMemoized = createSelector(
	[ getUiState, createIdentitySelector<UiState, IIndexSignature<string | number>>() ],
	getSiderFilters
);

// # endregion

const uiSlice = createAppSlice({
	name: "ui",
	initialState,
	reducers: {
		clearOrderFormAction: clearOrderFormReducer,
		setSelectedItemsAction: setSelectedItemsReducer,
		setScanDateOptionAction: setScanDateOptionReducer,
		setFilterAction: setFilterReducer
	},
	selectors: {
		selectAllFilters: getAllFilters,
		selectSiderFilters: getSiderFilterMemoized,
		selectSelectedItemIds: getSelectedItemIds,
		selectAllSelectedItems: getAllSelectedItems
	}
});

export default uiSlice;

export const {
	clearOrderFormAction, setSelectedItemsAction, setFilterAction, setScanDateOptionAction
} = uiSlice.actions;

export const {
	selectAllFilters, selectAllSelectedItems, selectSelectedItemIds, selectSiderFilters
} = uiSlice.selectors;
